import React, {useState, useEffect} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';

import FormFooter from '../common/FormFooter';
import Select from '../common/Select';
import Input from '../common/Input';
import Spinner from '../common/Spinner';

import {postAddress} from '../../api/api';
import useScrollTop from '../../hooks/useScrollTop';
import {formSteps, validations} from '../../constants/form';
import {doValidation} from '../../helpers/form-validation';
import {useDataProviderState, useDataProviderActions} from '../../providers/DataProvider';
import HighlightBox from "../common/HighlightBox.jsx";
import clearingImg from "../../assets/img/clearing.png";
import {TrackGoogleAnalyticsEvent} from "../../google-analytics/gaTracker.js";

export default function AddressStep() {

    const {data, loading, initialData, userData, uid} = useDataProviderState();
    const {nextStep, saveData, setLoading, setErrorMessage} = useDataProviderActions();

    const [formData, setFormData] = useState(data[formSteps.ADDRESS]);
    const [errors, setErrors] = useState({});

    const [cities, setCities] = useState([]);

    const formValidations = validations[formSteps.ADDRESS];

    useScrollTop();

    TrackGoogleAnalyticsEvent("AddressStep");

    useEffect(() => {
        if (formData.departamento) {
            const selectedState = initialData.departamento.find((eachDep) => eachDep.id == formData.departamento)
            setCities(selectedState.ciudades);
        } else {
            setCities([]);
        }
        setFormData({...formData, ciudad: ''});
    }, [formData.departamento]);

    function updateErrors(event, name) {
        setErrors({
            ...errors,
            [name]: doValidation(event, formValidations[name]),
        });
    }

    function handleFieldChange(event) {
        let {value, name, type} = event.target;

        if (formValidations[name].regexReplace) {
            if (formValidations[name].regexReplace.test(value)) {
                setFormData({...formData, [name]: value});
            }
        } else {
            setFormData({...formData, [name]: value});
        }

        if (errors[name] || type === 'select-one') {
            updateErrors(event, name);
        }
    }

    function handleFieldBlur(event) {
        const {name} = event.target;
        updateErrors(event, name);
    }

    function isFormValid() {
        const updatedErrors = {
            direccionParticular: doValidation({target: {value: formData.direccionParticular}}, formValidations.direccionParticular),
            numeroPuerta: doValidation({target: {value: formData.numeroPuerta}}, formValidations.numeroPuerta),
            apartamento: doValidation({target: {value: formData.apartamento}}, formValidations.apartamento),
            departamento: doValidation({target: {value: formData.departamento}}, formValidations.departamento),
            ciudad: doValidation({target: {value: formData.ciudad}}, formValidations.ciudad),
            telParticular: doValidation({target: {value: formData.telParticular}}, formValidations.telParticular),
            telExtra: doValidation({target: {value: formData.telExtra}}, formValidations.telExtra),
            departamentoNac: doValidation({target: {value: formData.departamentoNac}}, formValidations.departamentoNac),
        };

        setErrors(updatedErrors);

        return !Object.values(updatedErrors).some(Boolean);
    }

    async function handleNext() {
        if (isFormValid()) {
            setLoading(true);
            setErrors({});
            const response = await postAddress(formData, uid);

            if (response?.success) {
                saveData(formData, formSteps.ADDRESS);
                nextStep();
            } else if (response?.errors) {
                setErrors(response.errors);
            } else {
                setErrorMessage();
            }

            setLoading(false);
        }
    }

    return (
        <Container component="section" maxWidth="sm">

            <Box sx={{pb: 2}}>
                <HighlightBox>
                    <Box sx={{display: 'flex'}}>
                        <Box sx={{py: 2}}>
                            <Box className="color-primary" sx={{pl: 2, pb: 1, fontSize: '1.3rem'}}>
                                <b>{userData.nombre},</b>
                            </Box>
                            <Box as="span" sx={{
                                display: 'inline-block',
                                px: 2,
                                color: 'white',
                                background: 'var(--color-primary)',
                                borderBottomRightRadius: '15px',
                                borderTopRightRadius: '15px',
                                fontSize: '1.2rem'
                            }}>
                                ¡Ya estamos más cerca!
                            </Box>
                            <Box sx={{p: 1, pl: 2}}>
                                <b className="msg">Te solicitamos completes estos datos personales.</b>
                            </Box>
                        </Box>
                    </Box>
                </HighlightBox>
            </Box>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={12} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Dirección particular"
                        name="direccionParticular"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="N° puerta"
                        name="numeroPuerta"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Apartamento"
                        name="apartamento"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Select
                        label="Departamento"
                        name="departamento"
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                        formValidations={formValidations}
                        initialData={initialData}
                        handleFieldChange={handleFieldChange}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Select
                        label="Ciudad"
                        name="ciudad"
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                        formValidations={formValidations}
                        optionsOverride={cities}
                        handleFieldChange={handleFieldChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Teléfono particular"
                        name="telParticular"
                        placeholder="Ej: 24316589"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Teléfono extra"
                        name="telExtra"
                        placeholder="Ej: 24316589"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Select
                sx={{pb: 1}}
                label="Departamento de nacimiento"
                name="departamentoNac"
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
                formValidations={formValidations}
                initialData={initialData}
                handleFieldChange={handleFieldChange}
            />

            <FormFooter onNext={handleNext}/>

            <Spinner loading={loading}/>
        </Container>
    )

}
